import { Player, CourtResult } from '@/types/tournament';
import { assignPlayersToCourts, updateStandings } from '@/utils/tournamentLogic';
import { calculatePlayerPoints } from '@/utils/tournamentScoring';
import { toast } from 'sonner';
import { ExtendedTournamentState, AmericanoPlayer, AmericanoMatch } from './types';
import { generateAmericanoPartnerships, assignAmericanoCourts, computeAmericanoRoster, generateTeamAmericanoMatches } from './americanoLogic';
import { supabase as defaultSupabase, hasSupabaseAuth } from '@/integrations/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';
import { FunctionsHttpError } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

export const createSubmitRoundResults = (
  setTournamentState: React.Dispatch<React.SetStateAction<ExtendedTournamentState>>,
  t: (key: string, params?: any) => string,
  setShowLeaderboard?: (show: boolean) => void,
  supabaseClient?: SupabaseClient<Database>,
  getToken?: () => Promise<string | null>
) => {
  const supabase = supabaseClient || defaultSupabase;
  const formatError = (error: unknown) => {
    if (error instanceof Error) return error.message;
    try {
      if (typeof error === 'object' && error !== null) return JSON.stringify(error);
      return String(error);
    } catch {
      return String(error);
    }
  };
  return (roundResults: CourtResult[]) => {
    setTournamentState(prevState => {
      const expectedResults =
        prevState.tournamentType === "americano" ||
        prevState.tournamentType === "team_americano"
          ? prevState.americanoMatches.length
          : prevState.courts.length;

      if (roundResults.length !== expectedResults) {
        toast.error(t("tournament.mexicano.incomplete_results"));
        return prevState;
      }

      // Record matches in Supabase (async, don't block UI)
      if (prevState.tournamentType && prevState.courts.length > 0) {
        recordMatchesAsync(prevState, roundResults, t, supabase, getToken)
          .then(() => {
            console.log('🎯 Database recording completed successfully');
          })
          .catch((error) => {
            console.error('🚨 Database recording failed:', formatError(error));
          });
      } else {
        console.warn('⚠️ Skipping database recording - missing tournament type or courts');
      }
      
      // Update players with round scores
      let updatedPlayers = prevState.players.map(player => {
        const newPlayer = { ...player };
        
        // Find player's court and their score for this round
        prevState.courts.forEach((court, courtIndex) => {
          const result = roundResults[courtIndex];
          if (court.some(p => p.id === player.id)) {
            const isTeam1 = court.slice(0, 2).some(p => p.id === player.id);
            const roundScore = isTeam1 ? result.score[0] : result.score[1];
            
            // Update round scores array
            if (!newPlayer.roundScores) {
              newPlayer.roundScores = [];
            }
            newPlayer.roundScores[prevState.currentRound - 1] = roundScore;
          }
        });
        
        return newPlayer;
      });
      
      // Award 12 points to bench players (Mexicano)
      const courtPlayerIds = new Set(prevState.courts.flat().map(p => p.id));
      updatedPlayers = updatedPlayers.map(player => {
        // If player is not in any court, they're on the bench
        if (!courtPlayerIds.has(player.id)) {
          if (!player.roundScores) {
            player.roundScores = [];
          }
          // Award bench points
          player.roundScores[prevState.currentRound - 1] = 12;
        }
        return player;
      });
      
      // Calculate total points from round scores (fix for Mexicano scoring)
      const playersWithTotalPoints = updatedPlayers.map(player => ({
        ...player,
        points: player.roundScores ? calculatePlayerPoints(player.roundScores) : 0,
        // CRITICAL FIX: Update matchesPlayed based on roundScores for bench rotation
        matchesPlayed: player.roundScores ? player.roundScores.filter(score => score !== undefined && score !== null).length : 0
      }));
      
      // Update standings with the updated players
      const finalUpdatedPlayers = updateStandings(playersWithTotalPoints, roundResults);
      const updatedResults = [...prevState.results];
      updatedResults[prevState.currentRound - 1] = roundResults;
      
      // Store court assignments for this round with player information
      const updatedRoundCourts = [...prevState.roundCourts];
      const roundResultsWithPlayers = roundResults.map((result, courtIndex) => {
        const court = prevState.courts[courtIndex];
        return {
          ...result,
          players: {
            team1: {
              player1: court[0].name,
              player2: court[1].name
            },
            team2: {
              player1: court[2].name,
              player2: court[3].name
            }
          }
        };
      });
      updatedRoundCourts[prevState.currentRound - 1] = { courts: prevState.courts, results: roundResultsWithPlayers };
      
      console.log(`Storing Mexicano round ${prevState.currentRound} results:`, roundResultsWithPlayers);
      console.log(`Storing Mexicano round ${prevState.currentRound} court assignments:`, prevState.courts);
      
      // Check if this is the last round
      const isLastRound = prevState.currentRound >= prevState.totalRounds;
      
      if (isLastRound) {
        // Tournament is finished - show leaderboard
        toast.success(t('tournament.mexicano.tournament_complete'));
        if (setShowLeaderboard) {
          setShowLeaderboard(true);
        }
        
        return {
          ...prevState,
          players: finalUpdatedPlayers,
          results: updatedResults,
          roundCourts: updatedRoundCourts,
          isActive: false,
          isFinished: true,
          isRoundActive: false,
        };
      }
      
      // Always continue to next round - no limit on number of rounds
      // Check if the next round already has stored data - if so, preserve it
      const nextRoundIndex = prevState.currentRound; // currentRound is 1-based, array is 0-based
      let newCourts: Player[][];
      let playersWithBenchFlags: Player[];
      
      if (updatedRoundCourts[nextRoundIndex] && updatedRoundCourts[nextRoundIndex].courts.length > 0) {
        // Next round already has stored court assignments - preserve them
        const storedRound = updatedRoundCourts[nextRoundIndex];
        newCourts = storedRound.courts;
        // For stored rounds, extract bench flags the same way
        const courtPlayerIds = new Set(newCourts.flat().map(cp => cp.id));
        playersWithBenchFlags = finalUpdatedPlayers.map(player => ({
          ...player,
          isOnBench: !courtPlayerIds.has(player.id)
        }));
        console.log(`Preserving existing court assignments for round ${prevState.currentRound + 1}:`, newCourts);
      } else {
        // Generate new court assignments for the next round
        const { courts: generatedCourts, updatedPlayers: playersWithBench } = assignPlayersToCourts(finalUpdatedPlayers);
        newCourts = generatedCourts;
        playersWithBenchFlags = playersWithBench;
        // Store the new court assignments for the next round (only if not already stored)
        updatedRoundCourts[nextRoundIndex] = { courts: newCourts, results: [] };
        console.log(`Generated new court assignments for round ${prevState.currentRound + 1}:`, newCourts);
      }
      
      toast.success(`Starting round ${prevState.currentRound + 1}`);
      
      return {
        ...prevState,
        players: playersWithBenchFlags,
        courts: newCourts,
        results: updatedResults,
        roundCourts: updatedRoundCourts,
        currentRound: prevState.currentRound + 1,
        // Keep tournament active - no automatic ending
        isActive: true,
        isRoundActive: true, // Keep round active so results entry shows
      };
    });
  };
};

// Helper function to record matches asynchronously in the background
const recordMatchesAsync = async (
  tournamentState: ExtendedTournamentState,
  results: CourtResult[],
  t: (key: string, params?: any) => string,
  supabase: SupabaseClient<Database>,
  getToken?: () => Promise<string | null>
) => {
  try {
    console.log('🏓 Starting background recording for Mexicano tournament...');
    
    // Validate inputs
    if (!tournamentState.courts || tournamentState.courts.length === 0) {
      throw new Error('No court data available');
    }
    
    if (!results || results.length === 0) {
      throw new Error('No results data available');
    }

    const tournamentName = `${tournamentState.tournamentType?.toUpperCase()} - ${new Date().toLocaleDateString()}`;
    const isCompleted = tournamentState.currentRound >= tournamentState.totalRounds;
    const tournamentData = {
      name: tournamentName,
      tournament_type: 'mexicano',
      total_rounds: tournamentState.totalRounds,
      completed_rounds: tournamentState.currentRound,
      is_completed: isCompleted
    };

    console.log('📊 Tournament data prepared:', tournamentData);

    // Gather all matches from previous rounds and current round
    const previousMatches = tournamentState.roundCourts.flatMap((roundData, roundIndex) =>
      roundData.results.map(result => ({
        round_number: roundIndex + 1,
        court_number: result.court,
        team1_player1_name: result.players.team1.player1,
        team1_player2_name: result.players.team1.player2,
        team2_player1_name: result.players.team2.player1,
        team2_player2_name: result.players.team2.player2,
        team1_score: result.score[0],
        team2_score: result.score[1],
        winner_team: result.winner === 'team1' ? 1 : 2
      }))
    );

    const currentRoundMatches = results.map((result, index) => {
      const court = tournamentState.courts[index];

      if (!court || court.length < 4) {
        console.warn(`⚠️ Court ${index + 1} has incomplete player data:`, court);
        return null;
      }

      return {
        round_number: tournamentState.currentRound,
        court_number: result.court,
        team1_player1_name: court[0].name,
        team1_player2_name: court[1].name,
        team2_player1_name: court[2].name,
        team2_player2_name: court[3].name,
        team1_score: result.score[0],
        team2_score: result.score[1],
        winner_team: result.winner === 'team1' ? 1 : 2
      };
    }).filter(match => match !== null);

    const matches = [...previousMatches, ...currentRoundMatches];

    if (matches.length === 0) {
      throw new Error('No valid matches to record');
    }

    console.log('🎾 Matches prepared for recording:', matches);

    // Only record completed tournaments to avoid per-round duplicates
    if (!isCompleted) {
      console.log('⏭️  Tournament not completed yet - skipping record');
      return;
    }

    // Get authorization token for Edge Function
    const token = getToken ? await getToken() : null;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Call the Edge Function to record tournament
    const { data, error } = await supabase.functions.invoke('record-tournament', {
      body: { tournamentData: { ...tournamentData, matches } },
      headers
    });

    if (error) {
      // Check if it's an authorization error
      const errorMessage = (error instanceof Error) ? error.message : String(error);
      const looksUnauthorized =
        (error instanceof FunctionsHttpError && (error as any)?.context?.response?.status === 401) ||
        errorMessage.includes('401') ||
        errorMessage.toLowerCase().includes('unauthorized');

      if (looksUnauthorized) {
        // Silently skip recording in development - no error logs
        return;
      }
      
      // Only log non-auth errors
      console.error('❌ Edge Function error:', error);
      throw error;
    }

    console.log('✅ Mexicano tournament data successfully recorded:', data);

    const tournamentId = (data as any)?.tournament_id;
    if (tournamentId) {
      try {
        const existing = JSON.parse(localStorage.getItem('pastTournamentIds') || '[]');
        localStorage.setItem('pastTournamentIds', JSON.stringify([...existing, tournamentId]));
      } catch {
        // Ignore localStorage errors
      }
      try {
        window.dispatchEvent(new CustomEvent('tournament-recorded', {
          detail: { id: tournamentId, name: tournamentData.name }
        }));
      } catch {
        // Ignore window event errors
      }
    }

    try {
      const w = window as any;
      const now = Date.now();
      if (!w.__lastDataSavedToastAt || now - w.__lastDataSavedToastAt > 1000) {
        toast.success(t('tournament.data_saved'));
        w.__lastDataSavedToastAt = now;
      }
    } catch {
      // Ignore window access errors
    }
  } catch (error) {
    const errorMessage = (error instanceof Error) ? error.message : (() => { try { return JSON.stringify(error as any); } catch { return String(error); } })();
    console.error('💥 Error recording Mexicano tournament:', errorMessage);
    toast.error(`${t('tournament.data_save_failed')}: ${errorMessage}`);
    // Don't throw here as this is background operation
  }
};

const recordAmericanoMatchesAsync = async (
  tournamentName: string, 
  matches: AmericanoMatch[], 
  currentRound: number, 
  totalRounds: number, 
  t: (key: string, params?: any) => string,
  supabase: SupabaseClient<Database>,
  getToken?: () => Promise<string | null>
) => {
  try {
  console.log('🏓 Starting background recording for Americano tournament...');
  console.log('🔑 Supabase client provided:', !!supabase, 'Has auth provider:', hasSupabaseAuth());
    
    if (!matches || matches.length === 0) {
      throw new Error('No matches data available');
    }

    const isCompleted = currentRound >= totalRounds;

    const tournamentData = {
      name: tournamentName,
      tournament_type: 'americano',
      total_rounds: totalRounds,
      completed_rounds: currentRound,
      // Only mark as completed at the final round to avoid early failures and duplicate inserts
      is_completed: isCompleted
    };

    console.log('📊 Americano tournament data prepared:', tournamentData);

    // Convert Americano matches to the expected format
    const formattedMatches = matches.map((match, index) => ({
      round_number: currentRound,
      court_number: match.court,
      team1_player1_name: match.team1.player1.name,
      team1_player2_name: match.team1.player2.name,
      team2_player1_name: match.team2.player1.name,
      team2_player2_name: match.team2.player2.name,
      team1_score: match.score?.team1 || 0,
      team2_score: match.score?.team2 || 0,
      winner_team: (match.score?.team1 || 0) > (match.score?.team2 || 0) ? 1 : 2
    }));

    console.log('🎾 Americano matches prepared for recording:', formattedMatches);

    // Only record the tournament to the database when completed (matches from all rounds)
    if (!isCompleted) {
      console.log('⏭️  Americano tournament not completed yet - skipping record this round');
      return;
    }

    // Call the Edge Function to record tournament at completion
    // Persist across rounds: reuse tournament_id if one was created in a prior round
    let existingId: string | null = null;
    try {
      existingId = localStorage.getItem('currentTournamentId');
    } catch (error) {
      console.debug('Unable to read current tournament id from localStorage', error);
    }
    
    try {
      console.log('🔑 About to invoke Edge Function with authenticated client');
      
      // Get authorization token for Edge Function
      const token = getToken ? await getToken() : null;
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const { data, error } = await supabase.functions.invoke('record-tournament', {
        body: { tournamentData: { ...tournamentData, matches: formattedMatches, tournament_id: existingId || undefined } },
        headers
      });

      if (error) {
        // Check for 401 authorization errors FIRST - before any logging
        const errorMessage = (error instanceof Error) ? error.message : String(error);
        const is401 =
          (error instanceof FunctionsHttpError && (error as any)?.context?.response?.status === 401) ||
          errorMessage.includes('401') ||
          errorMessage.toLowerCase().includes('unauthorized');

        if (is401) {
          // Silently skip recording in development - no logs, no toasts
          return;
        }

        // Only log non-auth errors
        console.error('❌ Edge Function error:', error);
        console.error('❌ Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ Americano tournament data successfully recorded:', data);

      const tournamentId = (data as any)?.tournament_id;
      if (tournamentId) {
        try {
          const existing = JSON.parse(localStorage.getItem('pastTournamentIds') || '[]');
          localStorage.setItem('pastTournamentIds', JSON.stringify([...existing, tournamentId]));
        } catch (error) {
          console.debug('Unable to persist past tournament ids', error);
        }
        try {
          localStorage.setItem('currentTournamentId', tournamentId);
        } catch (error) {
          console.debug('Unable to persist current tournament id', error);
        }
        try {
          window.dispatchEvent(new CustomEvent('tournament-recorded', {
            detail: { id: tournamentId, name: tournamentData.name }
          }));
        } catch (error) {
          console.debug('Failed to dispatch tournament-recorded event', error);
        }
      }

      try {
        const w = window as any;
        const now = Date.now();
        if (!w.__lastDataSavedToastAt || now - w.__lastDataSavedToastAt > 1000) {
          toast.success(t('tournament.data_saved'));
          w.__lastDataSavedToastAt = now;
        }
      } catch (error) {
        console.debug('Unable to show tournament saved toast', error);
      }
    } catch (error) {
      const errorMessage = (error instanceof Error) ? error.message : (() => { try { return JSON.stringify(error as any); } catch { return String(error); } })();
      console.error('💥 Detailed Americano error:', errorMessage);
      toast.error(`${t('tournament.data_save_failed')}: ${errorMessage}`);
      throw error;
    }
  } catch (error) {
    const errorMessage = (error instanceof Error) ? error.message : (() => { try { return JSON.stringify(error as any); } catch { return String(error); } })();
    console.error('💥 Error recording Americano tournament:', errorMessage);
    toast.error(`${t('tournament.data_save_failed')}: ${errorMessage}`);
    // Don't throw here as this is background operation
  }
};

export const createFinishAmericanoRound = (
  setTournamentState: React.Dispatch<React.SetStateAction<ExtendedTournamentState>>,
  t: (key: string, params?: any) => string,
  supabaseClient?: SupabaseClient<Database>,
  setShowLeaderboard?: (show: boolean) => void,
  getToken?: () => Promise<string | null>
) => {
  const supabase = supabaseClient || defaultSupabase;
  return (results: any[]) => {
    console.log('Finishing Americano round with results:', results);
    
    // Check if this will be the last round BEFORE updating state
    let willBeLastRound = false;

    setTournamentState(prev => {
      willBeLastRound = prev.currentRound >= prev.totalRounds;
      // The results are already in the correct format with score: {team1, team2}
      const updatedMatches = results.map(result => ({
        ...result
      }));

      let updatedPlayers = [...prev.americanoPlayers].map(p => ({
        ...p,
        points: 0, // Reset points before recalculating
        gamesPlayed: 0, // Reset games played
      }));

      // First, update round scores and partnerships from the results
      updatedMatches.forEach(match => {
        if (match.score) {
          const { team1, team2 } = match;
          const { team1: score1, team2: score2 } = match.score;

          // Team 1
          [team1.player1, team1.player2].forEach(player => {
            const playerIndex = updatedPlayers.findIndex(p => p.id === player.id && p.name === player.name);
            if (playerIndex !== -1) {
              if (!updatedPlayers[playerIndex].roundScores) {
                updatedPlayers[playerIndex].roundScores = [];
              }
              updatedPlayers[playerIndex].roundScores![prev.currentRound - 1] = score1;

              const partnerId = player.id === team1.player1.id ? team1.player2.id : team1.player1.id;
              if (!updatedPlayers[playerIndex].partnerships.includes(partnerId)) {
                updatedPlayers[playerIndex].partnerships.push(partnerId);
              }
            }
          });

          // Team 2
          [team2.player1, team2.player2].forEach(player => {
            const playerIndex = updatedPlayers.findIndex(p => p.id === player.id && p.name === player.name);
            if (playerIndex !== -1) {
              if (!updatedPlayers[playerIndex].roundScores) {
                updatedPlayers[playerIndex].roundScores = [];
              }
              updatedPlayers[playerIndex].roundScores![prev.currentRound - 1] = score2;

              const partnerId = player.id === team2.player1.id ? team2.player2.id : team2.player1.id;
              if (!updatedPlayers[playerIndex].partnerships.includes(partnerId)) {
                updatedPlayers[playerIndex].partnerships.push(partnerId);
              }
            }
          });
        }
      });
      
      // Award 12 points to bench players (Americano)
      const playingPlayerIds = new Set(
        updatedMatches.flatMap(match => [
          match.team1.player1.id,
          match.team1.player2.id,
          match.team2.player1.id,
          match.team2.player2.id
        ])
      );

      updatedPlayers = updatedPlayers.map(player => {
        // If player is not in any match, they're on the bench
        if (!playingPlayerIds.has(player.id)) {
          if (!player.roundScores) {
            player.roundScores = [];
          }
          // Award bench points
          player.roundScores[prev.currentRound - 1] = 12;
        }
        return player;
      });

      // Now, recalculate total points and games played from the roundScores array
      updatedPlayers.forEach(player => {
        if (player.roundScores) {
          player.points = calculatePlayerPoints(player.roundScores);
          player.gamesPlayed = player.roundScores.filter(score => score !== undefined).length;
        }
      });

      // Store match assignments for this round with player information
      const updatedRoundAmericanoMatches = [...prev.roundAmericanoMatches];
      const roundResultsWithPlayers = updatedMatches.map(match => ({
        ...match,
        players: {
          team1: {
            player1: match.team1.player1.name,
            player2: match.team1.player2.name
          },
          team2: {
            player1: match.team2.player1.name,
            player2: match.team2.player2.name
          }
        }
      }));
      updatedRoundAmericanoMatches[prev.currentRound - 1] = { matches: prev.americanoMatches, results: roundResultsWithPlayers };

      // Store the completed matches for this round
      const updatedRoundResults = [...prev.americanoRoundResults];
      updatedRoundResults[prev.currentRound - 1] = updatedMatches;
      
      console.log(`Storing round ${prev.currentRound} results:`, updatedMatches);
      
      // Check if we've completed all rounds BEFORE generating next round
      const isLastRound = prev.currentRound >= prev.totalRounds;

      // Record Americano matches in Supabase (async, don't block UI)
      if (prev.tournamentType && updatedMatches.length > 0) {
        const tournamentName = `${prev.tournamentType?.toUpperCase()} - ${new Date().toLocaleDateString()}`;
        recordAmericanoMatchesAsync(tournamentName, updatedMatches, prev.currentRound, prev.totalRounds || 10, t, supabase, getToken)
          .then(() => {
            console.log('🎯 Americano database recording completed successfully');
          })
          .catch((error) => {
            console.error('🚨 Americano database recording failed:', error);
          });
      } else {
        console.warn('⚠️ Skipping Americano database recording - missing tournament type or matches');
      }
      
      if (isLastRound) {
        console.log('🏁 Tournament finished! Setting isFinished=true, isActive=false, isRoundActive=false');
        toast.success(t('tournament.complete') || 'Tournament completed!');
        
        const finalState = {
          ...prev,
          americanoPlayers: updatedPlayers,
          americanoRoundResults: updatedRoundResults,
          roundAmericanoMatches: updatedRoundAmericanoMatches,
          isActive: false,
          isFinished: true,
          isRoundActive: false
        };
        
        console.log('🏁 Final tournament state:', {
          isActive: finalState.isActive,
          isFinished: finalState.isFinished,
          isRoundActive: finalState.isRoundActive,
          currentRound: finalState.currentRound,
          totalRounds: finalState.totalRounds
        });
        
        return finalState;
      }
      
      const nextRound = prev.currentRound + 1;
      
      // Check if the next round already has stored data - if so, preserve it
      const nextRoundIndex = nextRound - 1; // rounds are 1-based, array is 0-based
      let newMatches;
      
      if (updatedRoundAmericanoMatches[nextRoundIndex] && updatedRoundAmericanoMatches[nextRoundIndex].matches.length > 0) {
        // Next round already has stored match assignments - preserve them
        newMatches = updatedRoundAmericanoMatches[nextRoundIndex].matches;
        console.log(`Preserving existing match assignments for round ${nextRound}:`, newMatches);
      } else {
        // Generate new match assignments for the next round
        if (prev.tournamentType === 'team_americano') {
          // Team Americano: Use FIXED teams, just rotate matchups
          if (!prev.teamAmericanoFixedTeams || prev.teamAmericanoFixedTeams.length === 0) {
            console.error('No fixed teams found for Team Americano!');
            throw new Error('Fixed teams not initialized for Team Americano');
          }
          
          // All players continue (no benching)
          const allPlayersNoBench = updatedPlayers.map(p => ({ ...p, isOnBench: false }));
          updatedPlayers = allPlayersNoBench;
          
          // Generate next round matchups using FIXED teams
          const partnerships = generateTeamAmericanoMatches(prev.teamAmericanoFixedTeams, nextRound);
          newMatches = assignAmericanoCourts(partnerships);
          console.log(`Generated Team Americano match assignments for round ${nextRound} with fixed teams:`, newMatches);
        } else {
          // Regular Americano: Use roster computation with bench rotation and rotating partnerships
          const { playing, updated } = computeAmericanoRoster(updatedPlayers);
          updatedPlayers = updated;
          const partnerships = generateAmericanoPartnerships(playing, nextRound);
          newMatches = assignAmericanoCourts(partnerships);
          console.log(`Generated Americano match assignments for round ${nextRound} (with benching):`, newMatches);
        }
        // Store the new match assignments for the next round (only if not already stored)
        updatedRoundAmericanoMatches[nextRoundIndex] = { matches: newMatches, results: [] };
      }

      // Tournament finished - show completion message and leaderboard
      if (isLastRound) {
        toast.success(t('tournament.complete') || 'Tournament completed!');
        
        return {
          ...prev,
          americanoPlayers: updatedPlayers,
          americanoRoundResults: updatedRoundResults,
          roundAmericanoMatches: updatedRoundAmericanoMatches,
          isActive: false,
          isFinished: true,
          isRoundActive: false
        };
      }

      // Generate next round matches
      toast.success(`Round ${prev.currentRound} completed! Round ${nextRound} started.`);

      return {
        ...prev,
        americanoPlayers: updatedPlayers,
        americanoRoundResults: updatedRoundResults,
        roundAmericanoMatches: updatedRoundAmericanoMatches,
        currentRound: nextRound,
        isRoundActive: true, // Keep round active so results entry shows
        americanoMatches: newMatches,
        // Keep tournament active - no automatic ending
        isActive: true,
        isFinished: false
      };
    });
    
    // Show leaderboard AFTER state update completes (outside setState callback)
    if (willBeLastRound) {
      console.log('🏁 Calling setShowLeaderboard(true) after state update');
      if (setShowLeaderboard) {
        setShowLeaderboard(true);
      } else {
        console.warn('⚠️ setShowLeaderboard not available');
      }
    }
  };
};
